/**
 * Периоды аналитики из состояния приложения.
 *
 * Считает ядро (`reportPeriods`); здесь сборка входа и то, что нужно
 * хранить:
 *
 * · у прихода — какое наступление дохода он закрывает (`link.occurrence`),
 *   решённое при записи прихода;
 * · какой доход задаёт периоды (`periodPins`);
 * · основа сравнения периода (`periodBaselines`) — одна на период.
 *
 * Записи принимают решения по свежим данным хранилища, а не по снимку
 * экрана: старая вкладка не должна закреплять отключённый источник или
 * размечать приход по дате, которую уже исправили.
 *
 * Горизонт главной и действующая раскладка отсюда не трогаются: это другие
 * вопросы, и у них свои расчёты.
 */
import {
  baselineFits, baselineOf, currentPeriod, factsWindowOf, incomeAssignments, isBefore, isKnown, occurrenceOf,
  ownIncomeOf, paydayPeriods, paydaySource, periodPlanLines, planPeriodOf, reportPeriods,
  type AcceptedPlan, type Answer, type FinancialPlan, type IncomeAssignment, type PeriodBaseline,
  type PeriodInput, type PlainDate, type ReportPeriod,
} from '@skoka/core';
import { budgetWith, modeOf } from './plan-version.js';
import {
  loadState, periodIncomes, saveBaselineOnce, settlePeriods, type AppState,
} from './store.js';
import { queued } from './db.js';

export { periodIncomes };

export const periodInput = (state: AppState): PeriodInput => ({
  today: state.today,
  ...(state.trackingStartedAt ? { since: state.trackingStartedAt } : {}),
  streams: state.incomeStreams,
  incomes: periodIncomes(state.transactions, state.expectedIncomes),
  stablePlans: state.stablePlans,
  stablePayouts: state.stablePayouts,
  pins: state.periodPins,
  choice: state.periodChoice ?? null,
});

export const periodsOf = (state: AppState): ReportPeriod[] => reportPeriods(periodInput(state));
export const currentPeriodOf = (state: AppState): ReportPeriod => currentPeriod(periodInput(state));
/**
 * Периоды самой большой зарплаты — для выбора «С зарплаты», когда главного
 * дохода нет. `undefined` — главный доход есть или зарплаты нет.
 */
export const paydayOf = (state: AppState): { periods: ReportPeriod[]; name: string; others: string[] } | undefined => {
  const input = periodInput(state);
  const src = paydaySource(input);
  return src ? { periods: paydayPeriods(input), name: src.name, others: src.others } : undefined;
};
/** Как приходы разнесены по зарплатам — спорные видно по `basis: 'schedule'`. */
export const assignmentsOf = (state: AppState): IncomeAssignment[] => incomeAssignments(periodInput(state));

/**
 * План периода: суммы — за срок, факты — по фактическим границам.
 *
 * С сохранённой основой сравнение идёт по её суммам **и её сроку**: перенос
 * зарплатного дня, задержка или позднее закрытие периода не меняют ответ
 * «укладываюсь ли я» при тех же тратах. Сегодняшний прогноз (`forecast`)
 * считается отдельно, по сегодняшнему сроку. Без основы оба — сегодняшний
 * расчёт.
 *
 * Основа, сохранённая до смены дохода, сравнению не годится (`baselineFits`):
 * её срок шёл по прежнему расписанию, с доходами соседнего периода.
 */
export function periodBudget(
  state: AppState, period: ReportPeriod, baseline: PeriodBaseline | null = fitBaselineOf(state, period),
): Answer<{ plan: FinancialPlan }> {
  const term = planPeriodOf(period);
  const stablePlan = period.kind === 'stable'
    ? state.stablePlans.find((p) => p.id === period.sourceId) ?? null
    : null;
  // Доход периода — его собственная зарплата или выплата, по связи, а не по
  // датам расписания внутри срока.
  const ownAmount = ownIncomeOf(period, periodInput(state), state.expectedIncomes);
  const own = ownAmount === null ? null
    : period.kind === 'salary' && period.sourceId
      ? { streamId: period.sourceId, amount: ownAmount }
      : { amount: ownAmount };
  const periodLines = isKnown(state.baselines.need)
    ? periodPlanLines({
      incomeStreams: state.incomeStreams,
      obligations: state.obligations,
      needBaseline: state.needBaseline,
      goals: state.goals,
      accounts: state.accounts,
      creditTerms: state.creditTerms,
      mode: modeOf(state),
      today: state.today,
      term: { from: term.from, to: term.to },
      stablePlan,
      own,
      receivedBefore: receivedBefore(state, term.from, period.kind === 'salary' ? period.sourceId : undefined),
    })
    : undefined;
  const compareTerm = baseline ? { from: baseline.term.from, to: baseline.term.to, label: term.label } : term;
  const accepted: AcceptedPlan | null = baseline
    ? {
      id: baseline.key, period: compareTerm, acceptedAt: baseline.savedAt,
      lines: baseline.lines, mode: baseline.mode, basis: [],
    }
    : null;
  const window = factsWindowOf(period, state.today);
  return budgetWith(state, accepted, compareTerm, {
    ...(periodLines ? { periodLines } : {}),
    factsFrom: window.from,
    factsTo: window.to,
    // Живут на выплаты себе: «пришло» — они, а заработок уходит в запас.
    ...(period.kind === 'stable' ? { livesOnPayouts: true } : {}),
  });
}

/** Сохранённая основа периода, если она годится для сравнения. */
export const fitBaselineOf = (state: AppState, period: ReportPeriod): PeriodBaseline | null => {
  const b = baselineOf(state.periodBaselines, period.key);
  return b && baselineFits(period, b) ? b : null;
};

/**
 * Наступления других доходов, деньги за которые пришли до начала срока.
 *
 * Подработка за 6 ноября, пришедшая 1 ноября, — в фактах прошлого периода;
 * план нового второй раз её не ждёт. Своя зарплата периода считается
 * отдельно (`ownIncomeOf`).
 */
function receivedBefore(state: AppState, from: PlainDate, own: string | undefined): Set<string> {
  const out = new Set<string>();
  for (const f of periodIncomes(state.transactions, state.expectedIncomes)) {
    if (f.kind !== 'regular' || !f.streamId || f.streamId === own || !isBefore(f.date, from)) continue;
    const a = occurrenceOf(state.incomeStreams.find((s) => s.id === f.streamId), f);
    if (a && !isBefore(a.occurrence, from)) out.add(`${f.streamId}:${a.occurrence}`);
  }
  return out;
}

/**
 * Записать, что решено про периоды, — без основы сравнения.
 *
 * Снимок экрана не используется: решение принимает хранилище по свежим
 * данным (`settlePeriods`). Вызывать можно при каждой загрузке и из
 * нескольких вкладок. Отвечает, записано ли что-нибудь.
 */
export const keepPeriod = (): Promise<boolean> => settlePeriods();

/**
 * Сохранить основу сравнения идущего периода, если её ещё нет.
 *
 * Суммы — план по датам периода (`periodPlanLines`): платежи срока, доход
 * его наступлений, обычные траты по дням. Считается по состоянию,
 * перечитанному из хранилища, а не по снимку экрана; одна основа на период
 * гарантирована записью «если ещё нет».
 */
export const keepPeriodBaseline = (now = new Date()): Promise<boolean> =>
  queued('periodBaselines:compute', async () => {
    const state = await loadState();
    const period = currentPeriodOf(state);
    if (baselineOf(state.periodBaselines, period.key)) return false;
    const term = planPeriodOf(period);
    const budget = periodBudget(state, period, null);
    if (!budget.known) return false;
    const lines = {} as PeriodBaseline['lines'];
    for (const [kind, line] of Object.entries(budget.plan.lines)) {
      lines[kind as keyof PeriodBaseline['lines']] = line.forecast;
    }
    return saveBaselineOnce({
      key: period.key,
      savedAt: now.toISOString(),
      term: { from: term.from, to: term.to },
      lines,
      mode: modeOf(state),
      // Открыли посреди периода — это сегодняшний снимок, а не план на начало.
      late: state.today > period.from,
      // Срок укорочен ожидаемой сменой — запомнить, на какую.
      ...(period.next && period.switchEnd ? { switchTo: period.next } : {}),
    });
  });
